/**
 * GraphQL queries and hooks — the division-scoped dashboard widgets added by
 * issue #1652 (part of epic #1650): Services by Lifecycle, Application
 * Portfolio Heatmap, and Cross-Division Dependencies. Division scoping is
 * resolved server-side by walking `parentTeamId` to the hierarchy root
 * (ADR-1254) — these hooks are just the query surface.
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';

/**
 * Query for the Services by Lifecycle widget (issue #1652). `scope`
 * accepts `"my-teams"`, `"my-division"` (default), or `"all"`
 * (Administrator only).
 */
export const GET_SERVICES_BY_LIFECYCLE = gql`
  query GetServicesByLifecycle($scope: String) {
    servicesByLifecycle(scope: $scope) {
      data {
        status
        count
        percentage
      }
      total
      scope
    }
  }
`;

export interface ServicesByLifecycleEntry {
  status: string;
  count: number;
  percentage: number;
}

export interface ServicesByLifecycleData {
  servicesByLifecycle: {
    data: ServicesByLifecycleEntry[];
    total: number;
    scope: string;
  };
}

export interface ServicesByLifecycleVariables {
  scope?: string;
}

/** Hook for the Services by Lifecycle widget's division-scoped breakdown. */
export function useServicesByLifecycle(
  scope?: string
): QueryResult<ServicesByLifecycleData, ServicesByLifecycleVariables> {
  return useQuery(GET_SERVICES_BY_LIFECYCLE, {
    variables: { scope },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Application Portfolio Heatmap widget (issue #1652).
 */
export const GET_PORTFOLIO_HEATMAP = gql`
  query GetPortfolioHeatmap {
    portfolioHeatmap {
      matrix {
        businessUnit
        production
        development
        deprecated
        total
      }
    }
  }
`;

export interface PortfolioHeatmapRow {
  businessUnit: string;
  production: number;
  development: number;
  deprecated: number;
  total: number;
}

export interface PortfolioHeatmapData {
  portfolioHeatmap: {
    matrix: PortfolioHeatmapRow[];
  };
}

/** Hook for the Application Portfolio Heatmap widget's division x lifecycle matrix. */
export function usePortfolioHeatmap(): QueryResult<PortfolioHeatmapData> {
  return useQuery(GET_PORTFOLIO_HEATMAP, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Cross-Division Dependencies widget (issue #1652).
 */
export const GET_CROSS_DIVISION_DEPENDENCIES = gql`
  query GetCrossDivisionDependencies {
    crossDivisionDependencies {
      edges {
        fromDivision
        toDivision
        dependencyCount
        criticalCount
      }
      totalCrossDivisionDeps
    }
  }
`;

export interface CrossDivisionDependencyEdge {
  fromDivision: string;
  toDivision: string;
  dependencyCount: number;
  criticalCount: number;
}

export interface CrossDivisionDependenciesData {
  crossDivisionDependencies: {
    edges: CrossDivisionDependencyEdge[];
    totalCrossDivisionDeps: number;
  };
}

/** Hook for the Cross-Division Dependencies widget's cross-division edge list. */
export function useCrossDivisionDependencies(): QueryResult<CrossDivisionDependenciesData> {
  return useQuery(GET_CROSS_DIVISION_DEPENDENCIES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Business Capability Map widget (issue #1653). `depth`
 * matches the spec's documented `?depth=2` example (L1+L2); the resolver
 * defaults to 2 when omitted.
 */
export const GET_CAPABILITY_MAP = gql`
  query GetCapabilityMap($depth: Int) {
    capabilityMap(depth: $depth) {
      capabilities {
        id
        name
        level
        serviceCount
        children {
          id
          name
          level
          serviceCount
          children {
            id
            name
            level
            serviceCount
          }
        }
      }
      gapCount
    }
  }
`;

export interface CapabilityMapNode {
  id: string;
  name: string;
  level: number;
  serviceCount: number;
  children: CapabilityMapNode[];
}

export interface CapabilityMapData {
  capabilityMap: {
    capabilities: CapabilityMapNode[];
    gapCount: number;
  };
}

export interface CapabilityMapVariables {
  depth?: number;
}

/** Hook for the Business Capability Map widget's hierarchy tree. */
export function useCapabilityMap(
  depth?: number
): QueryResult<CapabilityMapData, CapabilityMapVariables> {
  return useQuery(GET_CAPABILITY_MAP, {
    variables: { depth },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Capability Coverage widget (issue #1653).
 */
export const GET_CAPABILITY_COVERAGE = gql`
  query GetCapabilityCoverage {
    capabilityCoverage {
      matrix {
        capability
        divisions {
          division
          count
        }
      }
    }
  }
`;

export interface CapabilityCoverageDivisionCount {
  division: string;
  count: number;
}

export interface CapabilityCoverageRow {
  capability: string;
  divisions: CapabilityCoverageDivisionCount[];
}

export interface CapabilityCoverageData {
  capabilityCoverage: {
    matrix: CapabilityCoverageRow[];
  };
}

/** Hook for the Capability Coverage widget's capability x division matrix. */
export function useCapabilityCoverage(): QueryResult<CapabilityCoverageData> {
  return useQuery(GET_CAPABILITY_COVERAGE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Scan Linkage widget (issue #2000, ADR-2000): per-team share of
 * Services linked to a repository scan. `scope` accepts `"my-teams"`,
 * `"my-division"` (default), or `"all"` (Administrator only).
 */
export const GET_SCAN_LINKAGE = gql`
  query GetScanLinkage($scope: String) {
    scanLinkage(scope: $scope) {
      total
      linked
      percentage
      scope
      teams {
        teamId
        teamName
        total
        linked
        percentage
      }
    }
  }
`;

export interface ScanLinkageTeamEntry {
  teamId: string | null;
  teamName: string;
  total: number;
  linked: number;
  percentage: number;
}

export interface ScanLinkageData {
  scanLinkage: {
    total: number;
    linked: number;
    percentage: number;
    scope: string;
    teams: ScanLinkageTeamEntry[];
  };
}

export interface ScanLinkageVariables {
  scope?: string;
}

/** Hook for the Scan Linkage widget's per-team breakdown. */
export function useScanLinkage(scope?: string): QueryResult<ScanLinkageData, ScanLinkageVariables> {
  return useQuery(GET_SCAN_LINKAGE, {
    variables: { scope },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
